import { Plus } from 'lucide-react';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { ChampionshipListItem } from 'modules/championship/domain/interfaces/championship-list-item';
import { clearSelectedChampionship } from 'modules/championship/store/championship/actions';
import ChampionshipCard from 'modules/championship/ui/components/championship-card';
import FieldBackground from 'modules/championship/ui/components/field-background';
import { useChampionshipsList } from 'modules/championship/ui/hooks/use-championships-list';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { UDPagedList } from 'ud-ui/components/paged-list';

const SEARCH_PLACEHOLDER = 'Поиск по турнирам';

const ChampionshipsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setHeaderContent } = useMainLayout();
  const { info } = useOrganizer();

  const { items, state, page, totalPages, perPage, draftSearch, setPage, setPerPage, setSearch, buildPageHref } =
    useChampionshipsList();

  const goToChampionshipCreate = useCallback(() => {
    dispatch(clearSelectedChampionship());
    navigate(Pages.CHAMPIONSHIPS.CREATE);
  }, [dispatch, navigate]);

  const goToEdit = useCallback(
    (id: ChampionshipListItem['id']) => {
      navigate(Pages.CHAMPIONSHIPS.builders.edit(id));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(
      <>
        <UDHeaderTitle>Турниры</UDHeaderTitle>
        <UDButton variant="primary" className="ml-6 flex items-center" onClick={goToChampionshipCreate}>
          <Plus size={16} className="mr-2" color={theme.colors.DarkBlue} />
          {t('championships.createTournamentButtonTitle')}
        </UDButton>
      </>,
    );

    return () => {
      setHeaderContent(null);
    };
  }, [goToChampionshipCreate, setHeaderContent, t]);

  // Город показывается по первой локации соревнования, а если её нет — по адресу
  // самой организации: адрес приходит отдельным запросом и в списке его нет.
  const renderChampionship = useCallback(
    (championship: ChampionshipListItem) => {
      const location = championship.locations?.[0]?.address;
      const address = location ? { city: location.city, country: location.country } : info?.address;

      return (
        <ChampionshipCard
          championship={{ ...championship, address }}
          containerProps={{ className: 'h-full' }}
          onClickCard={goToEdit}
        />
      );
    },
    [goToEdit, info?.address],
  );

  return (
    <div className="relative flex h-full flex-col">
      <FieldBackground />

      <UDPagedList<ChampionshipListItem>
        className="relative z-1"
        items={items}
        renderItem={renderChampionship}
        getItemKey={(championship) => championship.id}
        itemsClassName="grid grid-cols-3 gap-3"
        skeletonRowHeight={128}
        state={state}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        pageHref={buildPageHref}
        search={draftSearch}
        onSearchChange={setSearch}
        searchPlaceholder={SEARCH_PLACEHOLDER}
        emptyText={t('championships.emptyList.title')}
        emptySubtitle={t('championships.emptyList.body')}
        emptyAction={
          <UDButton variant="primary" className="flex flex-row items-center" onClick={goToChampionshipCreate}>
            <Plus size={16} className="text-DarkBlue mr-2" />
            {t('championships.createTournamentButtonTitle')}
          </UDButton>
        }
        notFoundText={t('championships.emptyList.notFoundTitle')}
      />
    </div>
  );
};

export default ChampionshipsListPage;
